import { useEffect, useState } from "react";
import { useRouter } from 'next/router';
// hooks
import useResponsive from "../../hooks/useResponsive";
// @mui
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
// components
import Iconify from '../../components/Iconify';
// utils
import { fKrMeetingTodoTimeKST } from '../../utils/formatTime';
// routes
import { PATH_JOBS } from '../../routes/paths';

// --------------------------------------------------
export default function BizMeetingTodo({ jobPosts = [] }) {
  const [openFullList, setOpenFullList] = useState(false);
  const [todoData, setTodoData] = useState([]);
  const smUp = useResponsive("up", "sm");
  const router = useRouter();

  const initializeTodoData = async () => {
    const newTodoData = [];
    /**
     * 미팅 예정된 후보 찾기
     */
    jobPosts?.forEach((element) => {
      if (element?.candidate_set?.length > 0) {
        element.candidate_set.forEach((candidate) => {
          const todo = {};
          if (candidate.status === "waiting-3way-meeting" || candidate.status === "confirm-3way-meeting") {
            const activeMeetings = candidate?.meet_calendars?.flatMap(cal =>
              cal.schedules?.filter(s => s.scheduled_at && !s.cancelled_at) || []
            );
            if (activeMeetings?.length > 0) {
              const meetingTime = new Date(activeMeetings[0].scheduled_at);
              const now = new Date();
              if (meetingTime >= now) {
                todo.meetingDate = `${fKrMeetingTodoTimeKST(meetingTime)}`;
                todo.step = "meeting";
                todo.jobPost = element.id;
                todo.candidate = candidate.id;
                todo.position = element?.detail_role?.card_display_name || element?.detail_role?.role || element?.get_position_display
                todo.crew = candidate?.crew?.nickname;
              }
            }
          };
          if (Object.keys(todo).length > 0) {
            newTodoData.push(todo);
          }
        });
      }
    });
    setTodoData(newTodoData);
  };

  useEffect(() => {
    if (jobPosts?.length > 0) {
      initializeTodoData();
    }
  }, [jobPosts]);

  const openFullListDialog = () => {
    setOpenFullList(true);
  };

  const todoAction = (todo)  => {
    const { jobPost, candidate } = todo;
    router.push({pathname: PATH_JOBS.root, query: {jid: jobPost, cid: candidate} });
  }
  

  return (
    <Card
      sx={{
        minWidth: smUp ? "390px" : 1,
        width: "100%",
        maxWidth: "100%",
        height: 1,
        borderRadius: 1.5,
        border: 1,
        borderColor: "rgba(145, 158, 171, 0.24)",
        boxShadow: "none",
        bgcolor: "rgba(0, 0, 0, 0.03)",
      }}
    >
      <Stack direction="row" aligntitems="center" justifyContent="space-between" sx={{ px: 3, pt: 2.5, mb: 0.5 }}>
        <Typography variant="subtitle2">
          예정된 미팅
        </Typography>
        {todoData?.length > 0 && (
        <Button
          variant="text"
          size="small"
          endIcon={<Iconify icon={'ic:chevron-right'} sx={{ ml: -1 }} />}
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            fontWeight: "normal",
          }}
          onClick={() => openFullListDialog()}
          disableFocusRipple
        >
          전체 보기 {todoData?.length}
        </Button>
        )}
      </Stack>
      {todoData?.length === 0 ? (
        <Stack sx={{ my: { xs: 2, md: 6 } }} alignItems={"center"}>
          <Iconify icon={'eva:info-fill'} sx={{ mb: 1, width: 32, height: 32, p: 1, bgcolor: "#00000014", color: "text.secondary", borderRadius: 2, fontSize: 16 }} />
          <Typography variant="overline">
            예정된 미팅이 없어요.
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            미팅이 생기면 알려드릴게요!
          </Typography>
        </Stack>
      ):(
        <Stack sx={{ px: 1.5, pb: 1.5 }}>
          {todoData.slice(0, 3).map((todo, index) => (
            <Stack key={index} direction="row" aligntitems="center" justifyContent="space-between"
              sx={{ 
                px: 1,
                py: 0.75,
                "&:hover": {
                  bgcolor: "rgba(145, 158, 171, 0.08)",
                  borderRadius: 1.5,
                  cursor: "pointer",
                }
              }}
              spacing={1.5}
              onClick={() => todoAction(todo)}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Divider orientation="vertical" flexItem
                  sx={{ 
                    borderRadius: 4,
                    borderWidth: 2,
                    backgroundColor: 'text.secondary'
                  }}
                />
                <Stack sx={{ py: 0.5 }} spacing={0.5}>
                  <Typography variant="overline">
                    {todo?.meetingDate}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {todo?.position} | {todo?.crew} 크루
                  </Typography>
                </Stack>
              </Stack>
              <Stack justifyContent={"center"}>
                <IconButton size="small" color="primary" edge="start" sx={{ color: "text.secondary" }}>
                  <Iconify icon={'ic:chevron-right'} />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      <FullListDialog isOpen={openFullList} close={() => setOpenFullList(false)} todoList={todoData} todoAction={(todo) => todoAction(todo)} />
    </Card>
  );
}

function FullListDialog({ isOpen, close, todoList = [], todoAction }) {
  const smUp = useResponsive('up', 'sm');
  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="dg" fullScreen={!smUp}>
      <DialogContent>
        {!smUp && (
          <IconButton size="medium" color="primary" onClick={close} edge="start">
            <Iconify icon={'ic:chevron-left'} />
          </IconButton>
        )}
        <Stack direction="row" aligntitems="center" justifyContent="space-between" sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5">
              예정된 미팅
            </Typography>
            <Typography variant="body1">
              {todoList.length}
            </Typography>
          </Stack>
          <IconButton size="medium" color="primary" onClick={close} sx={{ visibility: smUp ? "visible" : "hidden" }}>
            <Iconify icon={'eva:close-fill'} />
          </IconButton>
        </Stack>
        <Stack>
          {todoList.map((todo, index) => (
            <Stack key={index} direction="row" aligntitems="center" justifyContent="space-between"
              sx={{ 
                px: 1,
                py: 0.75,
                "&:hover": {
                  bgcolor: "rgba(145, 158, 171, 0.08)",
                  borderRadius: 1.5,
                  cursor: "pointer",
                }
              }}
              spacing={1.5}
              onClick={() => { close(); todoAction(todo) }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Divider orientation="vertical" flexItem
                  sx={{ 
                    borderRadius: 4,
                    borderWidth: 2,
                    backgroundColor: 'text.secondary'
                  }}
                />
                <Stack sx={{ py: 0.5 }} spacing={0.5}>
                  <Typography variant="overline">
                    {todo?.meetingDate} 
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {todo?.position} | {todo?.crew} 크루
                  </Typography>
                </Stack>
              </Stack>
              <Stack justifyContent={"center"}>
                <IconButton size="small" color="primary" edge="start" sx={{ color: "text.secondary" }}>
                  <Iconify icon={'ic:chevron-right'} />
                </IconButton>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
